import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiThermometer, FiDroplet, FiZap } from 'react-icons/fi';
import { TbGauge } from 'react-icons/tb';

const iconMap = {
  thermometer: FiThermometer,
  droplet: FiDroplet,
  zap: FiZap,
  gauge: TbGauge,
};

function CoreProductClassesStrip() {
  const { t } = useTranslation();

  // Default product classes in English - Section 1: Delivered System Classes
  const defaultProductClasses = [
    {
      icon: 'thermometer',
      kicker: '1–660 kW class (representative)',
      title: 'Thermal Management Systems',
      oneLiner: 'Vapor-compression and liquid-cooling architectures engineered for continuous duty, stable control, and acceptance-ready verification.',
      bullets: [
        'Compressor staging strategy + electronic expansion valve (EEV) control + superheat/subcooling stability design',
        'Heat exchanger and airflow architecture (condenser/evaporator) engineered for varying ambient/heat-load conditions',
        'Instrumentation, safety interlocks, and control logic aligned to predictable FAT/ATP results and evidence closure',
        'Serviceability-aware packaging: access planning, maintainability provisions, and fault-isolation approach (program dependent)'
      ],
      deliveredProducts: [
        'Shelter Cooling Units (SCU)',
        'Liquid Cooling Units (LCU)',
        'Air Ventilation & Conditioning Systems',
        'Electronics Cooling Packages'
      ],
      deployedFor: 'Deployed for radar systems, ground-based electronics, communication shelters, and high-power transmission equipment.'
    },
    {
      icon: 'droplet',
      kicker: 'Distribution skids + piping networks',
      title: 'Hydronic (Liquid Circulation) Networks',
      oneLiner: 'Conditioned fluid distribution engineered for controlled flow, pressure stability, reliability, and maintainable field operation.',
      bullets: [
        'Pump sizing and NPSH discipline + flow balancing + manifold architecture (supply/return)',
        'Filtration strategy + instrumentation integration (pressure/flow/temperature) and health monitoring provisions',
        'ΔP management, isolation/bypass logic, and controlled coupling interfaces for platform integration',
        'Leak-prevention design discipline: service access discipline, sealing strategy, and maintainability-first layouts'
      ],
      deliveredProducts: [
        'Coolant Circulation Skids',
        'Chilled Water Distribution Systems',
        'Liquid-to-Liquid Heat Exchanger Units',
        'Flow Balancing & Manifold Assemblies'
      ],
      deployedFor: 'Applied in liquid-cooled electronics, ground support equipment, and platform thermal management systems.'
    },
    {
      icon: 'zap',
      kicker: 'Servo + PLC/HMI systems (representative)',
      title: 'Electrical Panels & Motion Control (with SCADA Health Layer)',
      oneLiner: 'Panels engineered for traceable wiring, protection reliability, and field diagnostics — including motion control where applicable.',
      bullets: [
        'Servo azimuth/elevation and motion-control architectures (program dependent), with safety logic and interlocks',
        'Protection, grounding/EMI philosophy, and maintainable wiring discipline aligned to field reliability',
        'SCADA/HMI as the "eyes of the system": condition monitoring, state visibility, alarms/events, and diagnostics for maintainability and availability',
        'In-house panel build and integration discipline; aerospace/defense-grade harnessing capability deliverable where required'
      ],
      deliveredProducts: [
        'Antenna Positioning Control Panels',
        'Motion Control Cabinets',
        'Power Distribution Units (PDU)',
        'PLC/HMI Control Stations'
      ],
      deployedFor: 'Deployed in radar pedestals, tracking systems, ground support equipment, and automated test setups.'
    },
    {
      icon: 'gauge',
      kicker: 'Up to 300 bar class (representative)',
      title: 'High-Pressure Regulation Systems',
      oneLiner: 'Regulated pneumatic assemblies engineered for predictable pressure delivery, safety logic, and validation-driven acceptance.',
      bullets: [
        'Regulation strategy including relief approach, controlled set-point behavior, and fail-safe logic (program dependent)',
        'Cleanliness/contamination control discipline and leak-integrity design embedded into build execution',
        'Test and validation discipline aligned to measurable acceptance criteria and evidence readiness',
        'Serviceability planning: maintainable layouts, isolation strategy, and safe handling provisions'
      ],
      deliveredProducts: [
        'Pressure Regulation Panels',
        'Canister Pressurization Units',
        'Pneumatic Distribution Assemblies',
        'High-Pressure Test Stations'
      ],
      deployedFor: 'Applied in launcher ground support, pneumatic actuation systems, and pressure validation equipment.'
    }
  ];

  // Get translated content
  const sectionTitle = t('coreProductClasses.title', { defaultValue: 'Delivered System Classes (Representative) — Built-to-Spec Capability' });
  const sectionSubtitle = t('coreProductClasses.subtitle', { defaultValue: 'HADEN delivers program-critical electromechanical systems for defense environments. The system classes below summarize delivered capability ranges and the core engineering building blocks typically embedded inside each system class.' });
  const footnote = t('coreProductClasses.footnote', { defaultValue: 'Representative ranges shown. Exact architecture, verification scope, and qualification evidence are program-dependent.' });

  // Offered Variants content (moved from Section 2)
  const offeredVariantsTitle = t('enablers.offeredVariants.title', { defaultValue: 'Offered Variants (Program Dependent)' });
  const offeredVariantsSubtext = t('enablers.offeredVariants.subtext', { defaultValue: 'Beyond representative delivered configurations, HADEN engineers these system classes across multiple architectures and deployment constraints—tailored to program acceptance criteria.' });
  const offeredVariantsFootnote = t('enablers.offeredVariants.footnote', { defaultValue: 'Exact configuration, qualification standards alignment, and evidence scope are governed by customer program requirements and contractual documentation.' });

  // Default offered variants list
  const defaultOfferedVariants = [
    {
      category: 'Thermal Management Systems',
      details: 'liquid-to-liquid, liquid-to-refrigerant, air-to-refrigerant, conditioned air delivery for controlled environments (application dependent)'
    },
    {
      category: 'Hydronic Circulation Networks',
      details: 'single-loop and multi-branch distribution skids, filtration and flow conditioning, field-deployable piping networks, seawater interfacing where required (program dependent)'
    },
    {
      category: 'Electrical Panels & Motion Control',
      details: 'power distribution and protection panels, servo motion-control panels, PLC/HMI + SCADA health monitoring layer, aerospace/defense-grade harnessing deliverables (as applicable)'
    },
    {
      category: 'High-Pressure Regulation Systems',
      details: 'regulation and distribution assemblies, purge/charging and controlled pressurization logic, safety interlocks, leak-integrity validation support (application dependent)'
    }
  ];

  const offeredVariants = t('enablers.offeredVariants.items', { returnObjects: true, defaultValue: defaultOfferedVariants });

  // Get labels for product section
  const deliveredProductsLabel = t('coreProductClasses.deliveredProductsLabel', { defaultValue: 'DELIVERED PRODUCT EXAMPLES' });
  const deployedForPrefix = t('coreProductClasses.deployedForPrefix', { defaultValue: 'Deployed for' });

  // Get translated tiles - merge with default to preserve deliveredProducts
  const translatedTiles = t('coreProductClasses.tiles', { returnObjects: true });
  const translatedDeliveredProducts = t('coreProductClasses.deliveredProducts', { returnObjects: true });
  const translatedDeployedFor = t('coreProductClasses.deployedFor', { returnObjects: true });
  
  const productClasses = Array.isArray(translatedTiles) 
    ? translatedTiles.map((tile, i) => ({
        ...tile,
        icon: defaultProductClasses[i]?.icon || 'thermometer',
        deliveredProducts: Array.isArray(translatedDeliveredProducts) && translatedDeliveredProducts[i] 
          ? translatedDeliveredProducts[i] 
          : defaultProductClasses[i]?.deliveredProducts || [],
        deployedFor: Array.isArray(translatedDeployedFor) && translatedDeployedFor[i] 
          ? translatedDeployedFor[i] 
          : defaultProductClasses[i]?.deployedFor || ''
      }))
    : defaultProductClasses;

  return (
    <div 
      id="product-classes" 
      className="py-12 md:py-16"
      style={{ backgroundColor: '#F8FAFC' }}
      data-testid="core-product-classes-strip"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="text-center mb-8"
        >
          <h2 
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{ color: '#101957' }}
          >
            {sectionTitle}
          </h2>
          <p 
            className="text-sm md:text-base max-w-3xl mx-auto"
            style={{ color: '#475569' }}
          >
            {sectionSubtitle}
          </p>
        </motion.div>

        {/* 4 Tiles Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 items-stretch">
          {productClasses.map((product, index) => {
            const Icon = iconMap[product.icon] || FiThermometer;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, ease: "easeOut", delay: index * 0.08 }}
                className="bg-white rounded-lg p-5 flex flex-col h-full"
                style={{ 
                  border: '1px solid rgba(16, 25, 87, 0.08)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
                }}
                data-testid={`product-tile-${index}`}
              >
                {/* Icon */}
                <div 
                  className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                  style={{ backgroundColor: 'rgba(16, 25, 87, 0.06)' }}
                >
                  <Icon className="text-lg" style={{ color: '#101957' }} />
                </div>

                {/* Kicker */}
                <p 
                  className="text-xs font-medium mb-1 text-left"
                  style={{ color: '#64748b' }}
                >
                  {product.kicker}
                </p>

                {/* Title */}
                <h3 
                  className="text-sm font-bold mb-2 leading-tight text-left"
                  style={{ color: '#0B1220' }}
                >
                  {product.title}
                </h3>

                {/* One-liner */}
                <p 
                  className="text-xs mb-3 leading-relaxed text-left"
                  style={{ color: '#475569' }}
                >
                  {product.oneLiner}
                </p>

                {/* Bullets */}
                <ul className="space-y-1.5 mb-4 flex-grow">
                  {product.bullets.map((bullet, bulletIndex) => (
                    <li 
                      key={bulletIndex}
                      className="flex items-start text-xs"
                      style={{ color: '#64748b' }}
                    >
                      <span 
                        className="mr-2 mt-1.5 w-1 h-1 rounded-full flex-shrink-0"
                        style={{ backgroundColor: '#101957' }}
                      />
                      <span className="leading-relaxed text-left">{bullet}</span>
                    </li>
                  ))}
                </ul>

                {/* Delivered Product Examples - Aligned to bottom with mt-auto */}
                {product.deliveredProducts && product.deliveredProducts.length > 0 && (
                  <div 
                    className="pt-3 mt-auto"
                    style={{ borderTop: '1px solid rgba(16, 25, 87, 0.08)' }}
                  >
                    <p 
                      className="text-xs font-bold mb-2 text-left"
                      style={{ color: '#101957' }}
                    >
                      {deliveredProductsLabel}
                    </p>
                    <ul className="space-y-1">
                      {product.deliveredProducts.map((deliveredProduct, dpIndex) => (
                        <li 
                          key={dpIndex}
                          className="flex items-center text-xs text-left"
                          style={{ color: '#334155' }}
                        >
                          <span 
                            className="mr-2 flex-shrink-0"
                            style={{ color: '#10b981' }}
                          >
                            ✓
                          </span>
                          <span>{deliveredProduct}</span>
                        </li>
                      ))}
                    </ul>
                    {product.deployedFor && (
                      <p 
                        className="text-xs mt-2 italic text-left"
                        style={{ color: '#94a3b8' }}
                      >
                        {product.deployedFor}
                      </p>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Footnote */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="text-center text-xs mb-8"
          style={{ color: '#94a3b8' }}
        >
          {footnote}
        </motion.p>

        {/* Offered Variants Block - Below footnote, above MIL-Grade Layer */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
          className="p-6 rounded-xl"
          style={{ 
            backgroundColor: 'rgba(16, 25, 87, 0.03)',
            border: '1px solid rgba(16, 25, 87, 0.08)'
          }}
          data-testid="offered-variants-block"
        >
          <h3 
            className="text-lg md:text-xl font-bold mb-2 text-left"
            style={{ color: '#101957' }}
          >
            {offeredVariantsTitle}
          </h3>
          <p 
            className="text-sm mb-4 text-left"
            style={{ color: '#475569' }}
          >
            {offeredVariantsSubtext}
          </p>

          {/* Tight list - no cards, no tooltips */}
          <ul className="space-y-3">
            {(Array.isArray(offeredVariants) ? offeredVariants : defaultOfferedVariants).map((variant, index) => (
              <li 
                key={index}
                className="flex items-start text-sm"
                style={{ color: '#334155' }}
              >
                <span 
                  className="mr-2 mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: '#101957' }}
                />
                <span className="leading-relaxed text-left">
                  <strong style={{ color: '#101957' }}>{variant.category}:</strong>{' '}
                  {variant.details}
                </span>
              </li>
            ))}
          </ul>

          {/* Footnote */}
          <p 
            className="mt-4 text-xs text-left"
            style={{ color: '#94a3b8' }}
          >
            {offeredVariantsFootnote}
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default CoreProductClassesStrip;
